import { useMemo } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { glide, pose } from "./Player";
import { archTap } from "./Walls";
import { BAND, EN, RAINBOW_ORDER, SECTIONS } from "../../lib/sections";
import { FEEL, MEMORY_SIGN } from "../../lib/world";
import { COLOURS, PETAL, releasePetals, stillFlowers } from "../../lib/petals";

const R = FEEL.w / 2;
const SPRING = FEEL.h - R;
const archShape = (() => {
  const s = new THREE.Shape();
  s.moveTo(-R, 0);
  s.lineTo(R, 0);
  s.lineTo(R, SPRING);
  s.absarc(0, SPRING, R, 0, Math.PI, false);
  s.lineTo(-R, 0);
  return s;
})();

const portalVert = `
uniform float uR; uniform float uH;
varying vec2 vUv; varying vec3 vW; varying vec2 vP;
void main() {
  vP = position.xy;
  vUv = vec2((position.x + uR) / (2.0 * uR), position.y / uH);
  vW = (modelMatrix * vec4(position, 1.0)).xyz;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;
const portalFrag = `
uniform vec3 uColor; uniform float uTime; uniform float uGlow; uniform float uShimmer; uniform float uSettle; uniform float uR; uniform float uH;
uniform float uWelcome; uniform sampler2D uPeek; uniform float uPeekOn;
varying vec2 vUv; varying vec3 vW; varying vec2 vP;
float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float n(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(h(i), h(i + vec2(1.0, 0.0)), f.x), mix(h(i + vec2(0.0, 1.0)), h(i + vec2(1.0, 1.0)), f.x), f.y); }
float archR(vec2 p) { float s = uH - uR; return p.y > s ? length(vec2(p.x, p.y - s)) / uR : abs(p.x) / uR; }
void main() {
  float strokes = n(vec2(vW.x * 3.0, vW.y * 0.8 + uTime * 0.15)) * 0.6 + n(vW.xy * 7.0 - uTime * 0.05) * 0.4;
  float sh = exp(-pow((vW.x - uShimmer) / 0.5, 2.0)) * 0.4;
  float r = archR(vP);
  float edge = smoothstep(0.62, 1.0, r);
  // A short passage: its far opening shrinks toward a vanishing point at eye height.
  vec2 vp = vec2(0.0, 1.45);
  vec2 pf = vp + (vP - vp) / 0.52;
  float far = (1.0 - smoothstep(0.9, 1.04, archR(pf))) * smoothstep(-0.06, 0.05, pf.y);
  float floorM = 1.0 - smoothstep(-0.05, 0.05, pf.y);
  float t = clamp((1.0 - r) / 0.48, 0.0, 1.0);
  vec3 warm = vec3(1.0, 0.92, 0.78);
  vec3 reveal = mix(uColor * 0.62, mix(uColor, warm, 0.62), t * t);
  reveal = mix(reveal, uColor * 1.1, strokes * 0.35) + warm * 0.16 * t * (0.7 + 0.3 * strokes);
  vec3 floorC = mix(uColor * 0.8, warm, 0.3 + 0.55 * t);
  vec2 gu = vec2(pf.x / (2.0 * uR) + 0.5, clamp(pf.y / uH, 0.0, 1.0) * 0.86 + 0.07);
  vec3 peek = texture2D(uPeek, gu).rgb;
  vec3 glimpse = mix(warm, peek, uPeekOn * 0.8) * (1.0 + 0.3 * uWelcome) + 0.06 * (1.0 - smoothstep(0.0, 1.0, archR(pf)));
  vec3 col = mix(reveal, floorC, floorM * (1.0 - far));
  col = mix(col, glimpse, far);
  col += (uGlow * 0.12 + sh + uSettle * 0.3 + uWelcome * 0.08) * vec3(1.0, 0.95, 0.9);
  col = mix(col, uColor * 1.15 + 0.08, edge * 0.35);
  float inner = mix(0.66 + 0.2 * t, 0.95, far) + strokes * 0.04 + sh * 0.2 + uSettle * 0.15 + uWelcome * 0.06;
  float a = mix(inner, 0.5 + 0.1 * strokes, smoothstep(0.86, 1.0, r));
  gl_FragColor = vec4(min(col, vec3(1.0)), clamp(a, 0.0, 0.96));
  #include <colorspace_fragment>
}`;
// The arch's light spilling out onto the floor in front of it.
const poolFrag = `
uniform vec3 uColor; uniform float uWelcome; uniform float uTime;
varying vec2 vUv;
float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float n(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(h(i), h(i + vec2(1.0, 0.0)), f.x), mix(h(i + vec2(0.0, 1.0)), h(i + vec2(1.0, 1.0)), f.x), f.y); }
void main() {
  float d = length(vec2((vUv.x - 0.5) * 1.9, (1.0 - vUv.y) * 1.05));
  float wash = 0.75 + 0.25 * n(vUv * vec2(6.0, 4.0) + uTime * 0.03);
  // a touch brighter with a warm core, so each pool stays clear on the projected floor
  float a = (1.0 - smoothstep(0.05, 1.0, d)) * (0.8 + 0.2 * uWelcome) * wash;
  float core = 1.0 - smoothstep(0.0, 0.55, d);
  gl_FragColor = vec4(mix(mix(uColor, vec3(1.0, 0.93, 0.8), 0.25), vec3(1.0, 0.97, 0.9), 0.3 * core), min(0.92, a + 0.1 * core));
  #include <colorspace_fragment>
}`;
// A tiny, softened glimpse of the room's first painting far down the passage, in that room's light (96×128, built once).
const glimpses = {};
function glimpseTexture(id, img) {
  if (!img?.width) return null;
  if (glimpses[id]?.img === img) return glimpses[id].tex;
  const c = document.createElement("canvas");
  c.width = 96;
  c.height = 128;
  const g = c.getContext("2d");
  const bg = g.createRadialGradient(48, 58, 6, 48, 64, 92);
  bg.addColorStop(0, "#FFF1DC");
  bg.addColorStop(1, BAND[id]);
  g.fillStyle = bg;
  g.fillRect(0, 0, 96, 128);
  const k = Math.min(60 / img.width, 62 / img.height);
  const w = img.width * k, h = img.height * k;
  const tiny = document.createElement("canvas");
  tiny.width = Math.max(4, Math.round(w / 4));
  tiny.height = Math.max(4, Math.round(h / 4));
  tiny.getContext("2d").drawImage(img, 0, 0, tiny.width, tiny.height);
  g.fillStyle = "rgba(255,246,232,0.75)";
  g.fillRect(48 - w / 2 - 3, 56 - h / 2 - 3, w + 6, h + 6);
  g.imageSmoothingQuality = "high";
  g.globalAlpha = 1;
  g.drawImage(tiny, 48 - w / 2, 56 - h / 2, w, h);
  g.globalAlpha = 0.14;
  g.fillStyle = BAND[id];
  g.fillRect(0, 0, 96, 128);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearFilter;
  glimpses[id]?.tex.dispose();
  glimpses[id] = { img, tex };
  return tex;
}

function pathTexture(id) {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 512;
  const g = c.getContext("2d");
  let s = id.length * 97 + id.charCodeAt(0) * 13;
  const rnd = () => (s = (s * 16807) % 2147483647) / 2147483647;
  const wash = g.createLinearGradient(0, 0, 0, 512);
  wash.addColorStop(0, "rgba(246,234,214,0.8)");
  wash.addColorStop(1, "rgba(246,234,214,0)");
  g.fillStyle = wash;
  g.beginPath();
  g.moveTo(66, 0);
  g.bezierCurveTo(44, 200, 30, 380, 12, 512);
  g.lineTo(244, 512);
  g.bezierCurveTo(226, 380, 212, 200, 190, 0);
  g.fill();
  for (let i = 0; i < 4; i++) {
    const w = 118 - i * 8;
    g.save();
    g.translate(128 + (rnd() - 0.5) * 22, 62 + i * 116);
    g.rotate((rnd() - 0.5) * 0.3);
    g.fillStyle = `rgba(228,214,194,${0.9 - i * 0.17})`;
    g.beginPath();
    g.roundRect(-w / 2, -26, w, 52, 18);
    g.fill();
    g.strokeStyle = `rgba(168,148,128,${0.35 - i * 0.06})`;
    g.lineWidth = 2;
    g.stroke();
    g.restore();
  }
  const petal = new Path2D(PETAL);
  const cols = COLOURS[id];
  for (let i = 0; i < 26; i++) {
    const y = 10 + rnd() * 470;
    g.save();
    g.translate(44 + rnd() * 168, y);
    g.rotate(rnd() * 6.28);
    const k = 0.55 + rnd() * 0.6;
    g.scale(k, k);
    g.globalAlpha = 0.85 * (1 - y / 560);
    g.fillStyle = cols[i % cols.length];
    g.fill(petal);
    g.restore();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}
const ribbonFrag = `
uniform vec3 uColor; uniform float uT;
varying vec2 vUv;
void main() {
  float head = 1.0 - uT * 0.42;
  float a = smoothstep(head - 0.08, head + 0.04, vUv.y) * (1.0 - smoothstep(0.65, 1.0, uT));
  a *= 0.55 * (1.0 - abs(vUv.x - 0.5) * 1.6);
  gl_FragColor = vec4(uColor, max(a, 0.0));
  #include <colorspace_fragment>
}`;
const ribbonVert = `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;
// Painted flower garlands: a soft sway that grows towards the hanging ends, and a fuller bloom on hover.
const flowerVert = `
uniform float uTime; uniform float uSway; uniform float uBloom; uniform float uSeed;
varying vec2 vUv;
void main() {
  vUv = uv;
  vec3 p = position;
  float hang = 1.0 - uv.y;
  p.x += sin(uTime * 0.9 + uv.y * 4.0 + uSeed) * 0.018 * uSway * (0.3 + hang);
  p.y += sin(uTime * 0.7 + uv.x * 3.0 + uSeed) * 0.008 * uSway;
  p.xy *= 1.0 + 0.035 * uBloom;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
}`;
const flowerFrag = `
uniform sampler2D uMap; uniform float uBloom;
varying vec2 vUv;
void main() {
  vec4 c = texture2D(uMap, vUv);
  if (c.a < 0.02) discard;
  vec3 col = c.rgb;
  float l = dot(col, vec3(0.299, 0.587, 0.114));
  col = mix(vec3(l), col, 1.12 + 0.2 * uBloom) * (0.97 + 0.07 * uBloom);
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), min(1.0, c.a * 1.15));
  #include <colorspace_fragment>
}`;
const bloom = {};
const noRay = () => null;

function textTexture(w, h, draw) {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  const paint = () => {
    const g = c.getContext("2d");
    g.clearRect(0, 0, w, h);
    g.textAlign = "center";
    g.textBaseline = "middle";
    draw(g);
    tex.needsUpdate = true;
  };
  paint();
  Promise.all(['italic 600 132px "Cormorant Garamond"', 'italic 500 92px "Cormorant Garamond"', '600 62px "Caveat"', '600 120px "Caveat"', '400 96px "Caveat Brush"'].map((f) => document.fonts.load(f))).then(paint).catch(() => {});
  return tex;
}
// Playful hand lettering: each letter sits at its own small tilt and height, like it was written by hand.
function whimsy(g, text, cx, cy, size) {
  g.font = `400 ${size}px "Caveat Brush", "Caveat"`;
  g.textAlign = "left";
  const widths = [...text].map((ch) => g.measureText(ch).width);
  let x = cx - widths.reduce((a, b) => a + b, 0) / 2;
  [...text].forEach((ch, i) => {
    g.save();
    g.translate(x + widths[i] / 2, cy + Math.sin(i * 1.7) * 5);
    g.rotate(Math.sin(i * 2.3) * 0.06);
    g.fillText(ch, -widths[i] / 2, 0);
    g.restore();
    x += widths[i];
  });
  g.textAlign = "center";
}
// Writes the neon heading in, line by line, with a faint flicker on the line that is lighting up.
const headingFrag = `uniform sampler2D uMap; uniform float uP; uniform float uT; varying vec2 vUv;
void main() {
  vec4 c = texture2D(uMap, vUv);
  float line = vUv.y > 0.5 ? 0.0 : 1.0;
  float p = clamp(uP - line, 0.0, 1.0);
  float x = (vUv.x - 0.18) / 0.64;
  float a = 1.0 - smoothstep(p * 1.08 - 0.06, p * 1.08, x);
  float lighting = step(0.001, p) * (1.0 - step(0.999, p));
  float flick = 0.82 + 0.18 * step(0.35, fract(sin(floor(uT * 13.0) + line * 7.0) * 43758.5453));
  gl_FragColor = vec4(c.rgb, c.a * a * mix(1.0, flick, lighting));
  #include <colorspace_fragment>
}`;
const flat = (map) => new THREE.MeshBasicMaterial({ map, transparent: true, toneMapped: false, depthWrite: false });

function Arch({ id, i, onEnter, u, mats }) {
  const x = FEEL.xs[id];
  const base = archTap(id, (z) => {
    bloom[id] = 1;
    setTimeout(() => (bloom[id] = 0), 3000);
    releasePetals(z);
    onEnter(z);
  });
  const tap = {
    ...base,
    onPointerOver: () => (base.onPointerOver(), (bloom[id] = 1)),
    onPointerOut: () => (base.onPointerOut(), (bloom[id] = 0)),
  };
  return (
    <group position={[x, 0, FEEL.z + 0.03]} userData={{ feelArch: id }}>
      <mesh position={[0, 1.66, 0.07]} renderOrder={2} material={mats.flowers[i]} raycast={noRay} userData={{ archFlowers: id }}>
        <planeGeometry args={[3.25, 3.42, 8, 16]} />
      </mesh>
      <mesh material={mats.portal[i]} userData={{ arch: id }} {...tap}>
        <shapeGeometry args={[archShape, 32]} />
      </mesh>
      <mesh position={[0, SPRING, 0.02]} material={mats.trim}>
        <torusGeometry args={[R, 0.05, 8, 40, Math.PI]} />
      </mesh>
      <mesh position={[0, SPRING, 0.03]} material={mats.glow[i]}>
        <torusGeometry args={[R + 0.09, 0.06, 6, 48, Math.PI]} />
      </mesh>
      {[-1, 1].map((s) => (
        <group key={s}>
          <mesh position={[s * R, SPRING / 2, 0.02]} material={mats.trim}>
            <boxGeometry args={[0.1, SPRING, 0.06]} />
          </mesh>
          <mesh position={[s * (R + 0.09), SPRING / 2, 0.03]} material={mats.glow[i]}>
            <boxGeometry args={[0.12, SPRING, 0.01]} />
          </mesh>
        </group>
      ))}
      <mesh position={[0, FEEL.h + 0.6, 0.08]} renderOrder={3} material={mats.label[i]} userData={{ archLabel: id }} {...tap}>
        <planeGeometry args={[2.5, 0.72]} />
      </mesh>
      <mesh position={[0, 2.4, 0.06]} material={mats.ribbon[i]}>
        <planeGeometry args={[FEEL.w * 0.95, 4.8]} />
      </mesh>
      <mesh position={[0, 0.035, 0.2]} material={mats.sill} raycast={noRay}>
        <boxGeometry args={[FEEL.w + 0.4, 0.07, 0.42]} />
      </mesh>
      <mesh position={[0, 0.012, 1.65]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={1} material={mats.path[i]} raycast={noRay}>
        <planeGeometry args={[1.6, 2.6]} />
      </mesh>
      <mesh position={[0, 0.016, 1.45]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={2} material={mats.pool[i]} raycast={noRay}>
        <planeGeometry args={[3.6, 2.6]} />
      </mesh>
    </group>
  );
}

// The hall's main wall: five rainbow portals with clear wall between them, in rainbow order.
export function FeelingWall({ u, onEnter, onMemory, peeks }) {
  const { camera } = useThree();
  const mats = useMemo(() => {
    const trim = new THREE.MeshBasicMaterial({ color: "#FFF6E8", toneMapped: false });
    const portal = RAINBOW_ORDER.map((id) => new THREE.ShaderMaterial({
      vertexShader: portalVert,
      fragmentShader: portalFrag,
      transparent: true,
      uniforms: { uTime: u.uTime, uR: { value: R }, uH: { value: FEEL.h }, uColor: { value: new THREE.Color(BAND[id]) }, uGlow: { value: 0 }, uShimmer: { value: -99 }, uSettle: { value: 0 }, uWelcome: { value: 0 }, uPeek: { value: null }, uPeekOn: { value: 0 } },
    }));
    const pool = RAINBOW_ORDER.map((id) => new THREE.ShaderMaterial({ vertexShader: ribbonVert, fragmentShader: poolFrag, transparent: true, depthWrite: false, uniforms: { uColor: { value: new THREE.Color(BAND[id]) }, uWelcome: { value: 0 }, uTime: u.uTime } }));
    const path = RAINBOW_ORDER.map((id) => flat(pathTexture(id)));
    const sill = new THREE.MeshBasicMaterial({ color: "#E9DCC8", toneMapped: false });
    const glow = RAINBOW_ORDER.map((id) => new THREE.MeshBasicMaterial({ color: BAND[id], transparent: true, opacity: 0.5, depthWrite: false, toneMapped: false, blending: THREE.AdditiveBlending }));
    const ribbon = RAINBOW_ORDER.map((id) => new THREE.ShaderMaterial({ vertexShader: ribbonVert, fragmentShader: ribbonFrag, transparent: true, depthWrite: false, uniforms: { uColor: { value: new THREE.Color(BAND[id]) }, uT: { value: 1 } } }));
    const label = RAINBOW_ORDER.map((id) => flat(textTexture(1024, 300, (g) => {
      g.fillStyle = "#FFFAF4";
      g.beginPath();
      g.roundRect(40, 14, 944, 272, 120);
      g.fill();
      g.fillStyle = `#${new THREE.Color(BAND[id]).lerp(new THREE.Color("#3A2350"), 0.45).getHexString()}`;
      g.font = 'italic 600 120px "Cormorant Garamond"';
      g.fillText(SECTIONS[id].name, 512, 92);
      // the English meaning in the same ink as the name, then her line, both large enough to read on a phone
      g.fillStyle = "#3A2350";
      g.font = 'italic 600 92px "Cormorant Garamond"';
      g.fillText(EN[id], 512, 182);
      g.font = '600 58px "Cormorant Garamond"';
      g.fillText(SECTIONS[id].line, 512, 252);
    })));
    const heading = new THREE.ShaderMaterial({
      vertexShader: ribbonVert,
      fragmentShader: headingFrag,
      transparent: true,
      depthWrite: false,
      uniforms: { uMap: { value: textTexture(2048, 220, (g) => {
        const lines = [["Which feeling would you", 74], ["like to explore?", 164]];
        g.fillStyle = "#FFFFFF";
        [[34, "rgba(255,255,255,0.75)"], [16, "rgba(255,255,255,0.9)"], [5, "rgba(255,255,255,1)"]].forEach(([blur, col]) => {
          g.shadowBlur = blur;
          g.shadowColor = col;
          lines.forEach(([t, y]) => whimsy(g, t, 1024, y, 96));
        });
        g.shadowBlur = 0;
        g.fillStyle = "#FFFDF7";
        lines.forEach(([t, y]) => whimsy(g, t, 1024, y, 96));
      }) }, uP: { value: 2 }, uT: { value: 0 } },
    });
    // A soft, slightly darker frosted panel behind the neon so it reads in any light.
    const backing = flat(textTexture(2048, 280, (g) => {
      g.shadowBlur = 40;
      g.shadowColor = "rgba(40,28,58,0.45)";
      g.fillStyle = "rgba(58,44,78,0.46)";
      g.beginPath();
      g.roundRect(330, 40, 1388, 200, 100);
      g.fill();
      g.shadowBlur = 0;
      g.strokeStyle = "rgba(255,255,255,0.28)";
      g.lineWidth = 3;
      g.stroke();
    }));
    const sign = flat(textTexture(1024, 420, (g) => {
      g.fillStyle = "rgba(255,250,244,0.9)";
      g.beginPath();
      g.roundRect(20, 20, 984, 380, 60);
      g.fill();
      g.fillStyle = "#4A2D55";
      g.font = 'italic 500 92px "Cormorant Garamond"';
      g.fillText("the Memory Wall", 512, 150);
      g.fillStyle = "#8A4F9E";
      g.font = '600 60px "Caveat"';
      g.fillText("sit for a while  ↩", 512, 290);
    }));
    const post = new THREE.MeshBasicMaterial({ color: "#C9B08A", toneMapped: false });
    const loader = new THREE.TextureLoader();
    const flowers = RAINBOW_ORDER.map((id, i) => {
      const map = loader.load(`${process.env.PUBLIC_URL || ""}/flowers/${id}.webp`);
      map.colorSpace = THREE.SRGBColorSpace;
      map.anisotropy = 4;
      return new THREE.ShaderMaterial({
        vertexShader: flowerVert,
        fragmentShader: flowerFrag,
        transparent: true,
        depthWrite: false,
        uniforms: { uMap: { value: map }, uTime: u.uTime, uSway: { value: 1 }, uBloom: { value: 0 }, uSeed: { value: i * 1.7 } },
      });
    });
    return { trim, portal, glow, ribbon, label, heading, backing, sign, post, flowers, pool, path, sill };
  }, [u]);

  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    const chosen = !!sessionStorage.getItem("afae-chosen");
    const since = window.__afaeSettle ? (performance.now() - window.__afaeSettle) / 1000 : 99;
    const still = stillFlowers();
    RAINBOW_ORDER.forEach((id, i) => {
      const f = mats.flowers[i].uniforms;
      mats.flowers[i].visible = !window.__afaeNoFlowers;
      f.uSway.value = still ? 0 : 1;
      f.uBloom.value += ((bloom[id] || 0) - f.uBloom.value) * (still ? 1 : 0.08);
      const p = mats.portal[i].uniforms;
      const peek = glimpseTexture(id, peeks?.[id]?.image);
      p.uPeek.value = peek;
      p.uPeekOn.value = peek ? 1 : 0;
      p.uWelcome.value = f.uBloom.value;
      mats.pool[i].uniforms.uWelcome.value = f.uBloom.value;
      const breath = 0.5 + 0.5 * Math.sin(t * 1.6 - i * 1.1);
      p.uGlow.value = chosen ? 0.3 : breath;
      p.uShimmer.value = chosen ? -99 : -9 + ((t * 2.6) % 20);
      const k = Math.max(0, Math.min(1, (since - i * 0.18) / 2.2));
      mats.ribbon[i].uniforms.uT.value = k;
      p.uSettle.value = k > 0.4 && k < 1 ? Math.sin(((k - 0.4) / 0.6) * Math.PI) : 0;
      mats.glow[i].opacity = chosen ? 0.45 : 0.35 + breath * 0.45;
    });
    if (since < 3.2 && camera.aspect < 1 && !glide.on) pose.yaw = 0.62 * Math.cos(1.5 * Math.PI * Math.min(1, since / 3.2));
    mats.heading.uniforms.uP.value = still ? 2 : Math.min(2, Math.max(0, (since - 0.5) / 1.4));
    mats.heading.uniforms.uT.value = t;
    window.__afaeHeading = +mats.heading.uniforms.uP.value.toFixed(2);
  });

  return (
    <group data-testid="feeling-wall">
      <mesh position={[0, 4.4, FEEL.z + 0.035]} material={mats.backing} raycast={noRay}>
        <planeGeometry args={[8.0, 1.07]} />
      </mesh>
      <mesh position={[0, 4.4, FEEL.z + 0.04]} material={mats.heading} userData={{ feelHeading: true }}>
        <planeGeometry args={[8.0, 0.84]} />
      </mesh>
      {RAINBOW_ORDER.map((id, i) => (
        <Arch key={id} id={id} i={i} onEnter={onEnter} u={u} mats={mats} />
      ))}
      <group position={[MEMORY_SIGN.x, 0, MEMORY_SIGN.z]} rotation={[0, MEMORY_SIGN.rot, 0]} userData={{ memorySign: true }}>
        <mesh position={[0, 0.8, 0]} material={mats.post}>
          <boxGeometry args={[0.07, 1.6, 0.07]} />
        </mesh>
        <mesh position={[0, 1.75, 0.05]} material={mats.sign} {...archTap("memory", onMemory)}>
          <planeGeometry args={[1.5, 0.62]} />
        </mesh>
      </group>
    </group>
  );
}
